import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button, Table, Spinner, Card, Row, Col, Form, Badge, Alert, Modal, Pagination } from "react-bootstrap";
import Select from "react-select";
import { API_WEB_URLS } from "../../constants/constAPI";
import axios from "axios";

const api = axios.create({ baseURL: API_WEB_URLS.BASE });
const AUTH_USER_ID = 0;
const AUTH_TOKEN = "token";

// Only 4 Core Accounting Voucher Types (Sales & Purchase removed)
const VOUCHER_TYPES = [
  { id: 7, name: "Contra (F4)", short: "Contra", key: "F4", prefix: "CV", color: "info", icon: "bx-transfer-alt", desc: "Cash to Bank, Bank to Cash, Bank to Bank transfers (Only Cash & Bank accounts)" },
  { id: 4, name: "Payment (F5)", short: "Payment", key: "F5", prefix: "PV", color: "danger", icon: "bx-wallet", desc: "Cash/Bank payments for expenses, creditors, driver payouts (Credit side fixed to Cash/Bank)" },
  { id: 5, name: "Receipt (F6)", short: "Receipt", key: "F6", prefix: "RV", color: "success", icon: "bx-receipt", desc: "Cash/Bank receipts from customers, debtors, capital (Debit side fixed to Cash/Bank)" },
  { id: 6, name: "Journal (F7)", short: "Journal", key: "F7", prefix: "JV", color: "primary", icon: "bx-book-alt", desc: "Adjustment entries, depreciation, corrections, accruals" },
];

// Helper to determine if a ledger is a Cash or Bank account
const isCashOrBankLedger = (ledger) => {
  if (!ledger) return false;
  const gId = Number(ledger.ledgerGroupId || ledger.F_LedgerGroupMaster || 0);
  const lId = Number(ledger.ledgerId || ledger.Id || 0);
  const name = (ledger.ledgerName || ledger.Name || "").toLowerCase();
  const group = (ledger.ledgerGroupName || ledger.GroupName || "").toLowerCase();

  if (gId === 4 || gId === 5 || gId === 8 || lId === 32 || lId === 7 || lId === 8 || lId === 11) {
    return true;
  }
  if (group.includes("bank") || group.includes("cash in hand")) {
    return true;
  }
  if (name.includes("bank") || name.includes("cash")) {
    return true;
  }
  return false;
};

// React-Select custom styling for table integration
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "33px",
    height: "33px",
    fontSize: "0.85rem",
    borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
    borderRadius: "0.25rem",
    backgroundColor: "#fff",
  }),
  valueContainer: (base) => ({
    ...base,
    height: "33px",
    padding: "0 8px",
  }),
  input: (base) => ({
    ...base,
    margin: "0px",
    padding: "0px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "33px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.85rem",
    padding: "6px 10px",
    backgroundColor: state.isSelected ? "#0d6efd" : state.isFocused ? "#f8f9fa" : "#fff",
    color: state.isSelected ? "#fff" : "#212529",
  }),
};

const customSelectErrorStyles = {
  ...customSelectStyles,
  control: (base) => ({
    ...base,
    minHeight: "33px",
    height: "33px",
    fontSize: "0.85rem",
    borderColor: "#dc3545",
    borderRadius: "0.25rem",
    backgroundColor: "#fff",
  }),
};

const VoucherEntry = () => {
  const formRef = useRef(null);

  // Form State (New vs Edit)
  const [editingVoucherId, setEditingVoucherId] = useState(0);
  const [voucherType, setVoucherType] = useState(VOUCHER_TYPES[1]); // Default Payment (F5)
  const [voucherDate, setVoucherDate] = useState(new Date().toISOString().split("T")[0]);
  const [voucherNo, setVoucherNo] = useState("");
  const [narration, setNarration] = useState("Manual Entry");
  
  // Dr/Cr Multi-lines State
  const [rows, setRows] = useState([
    { id: 1, type: "Dr", ledgerId: "", debitAmount: "", creditAmount: "", lineNarration: "", touched: false },
    { id: 2, type: "Cr", ledgerId: "", debitAmount: "", creditAmount: "", lineNarration: "", touched: false },
  ]);

  // Master Data & History
  const [ledgers, setLedgers] = useState([]);
  const [recentVouchers, setRecentVouchers] = useState([]);
  const [loadingLedgers, setLoadingLedgers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [submittedAttempt, setSubmittedAttempt] = useState(false);

  // Search & Pagination State for History Table
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({ show: false, voucher: null, deleting: false });

  // Auto-generate voucher number preview (only when not editing)
  const generateVoucherNo = (typeObj, dateStr) => {
    const d = dateStr ? dateStr.replace(/-/g, "") : new Date().toISOString().split("T")[0].replace(/-/g, "");
    const randomSeq = Math.floor(100 + Math.random() * 900);
    return `${typeObj.prefix}-${d}-${randomSeq}`;
  };

  useEffect(() => {
    if (!editingVoucherId) {
      setVoucherNo(generateVoucherNo(voucherType, voucherDate));
    }
  }, [voucherType, voucherDate, editingVoucherId]);

  // ─── Fetch Ledgers ──────────────────────────────────────────────────────────
  const fetchLedgers = async () => {
    setLoadingLedgers(true);
    try {
      const res = await api.get(`${API_WEB_URLS.VOUCHER_ENTRY}/${AUTH_USER_ID}/${AUTH_TOKEN}/Ledgers`);
      const list = res?.data?.data || res?.data?.Data || [];
      if (Array.isArray(list) && list.length > 0) {
        setLedgers(list);
        setLoadingLedgers(false);
        return;
      }
    } catch (e) {
      console.warn("Direct voucher ledger endpoint unavailable, trying fallback:", e);
    }

    try {
      const resFallback = await api.get(`${API_WEB_URLS.LEDGER_REPORT_LIST}/${AUTH_USER_ID}/${AUTH_TOKEN}/List`);
      const rawList = resFallback?.data?.data?.dataList || resFallback?.data?.Data?.DataList || [];
      const mapped = rawList.map((lm) => ({
        ledgerId: lm.Id || lm.id || lm.LedgerId || 0,
        ledgerName: lm.LedgerName || lm.Name || "Ledger",
        ledgerCode: lm.LedgerCode || lm.Code || "",
        ledgerGroupId: lm.F_LedgerGroupMaster || lm.groupId || 0,
        ledgerGroupName: lm.LedgerGroupName || lm.GroupName || "General",
        groupCategory: lm.GroupCategory || lm.Category || "Assets",
      }));
      setLedgers(mapped);
    } catch (e2) {
      console.error("All ledger fetch attempts failed:", e2);
    }
    setLoadingLedgers(false);
  };

  // ─── Fetch Recent Manual Vouchers ──────────────────────────────────────────
  const fetchRecentVouchers = async () => {
    setLoadingRecent(true);
    try {
      const res = await api.get(`${API_WEB_URLS.VOUCHER_ENTRY}/${AUTH_USER_ID}/${AUTH_TOKEN}/Recent`, {
        params: { count: 100 },
      });
      const list = res?.data?.data || res?.data?.Data || [];
      const manualOnly = list.filter(
        (v) =>
          [4, 5, 6, 7].includes(Number(v.voucherTypeId)) &&
          ![1, 2, 3].includes(Number(v.voucherTypeId))
      );
      setRecentVouchers(manualOnly);
    } catch (e) {
      console.warn("Could not fetch recent manual vouchers:", e);
    }
    setLoadingRecent(false);
  };

  useEffect(() => {
    fetchLedgers();
    fetchRecentVouchers();
  }, []);

  // ─── Preset Defaults on Voucher Type Change ─────────────────────────────────
  const handleSelectVoucherType = (vt) => {
    setVoucherType(vt);
    setFeedback({ type: "", message: "" });
    setSubmittedAttempt(false);

    if (!editingVoucherId) {
      const bankCashLedger = ledgers.find(isCashOrBankLedger);
      const defaultBankId = bankCashLedger ? bankCashLedger.ledgerId : "";

      if (vt.id === 4) {
        setRows([
          { id: 1, type: "Dr", ledgerId: "", debitAmount: "", creditAmount: "", lineNarration: "", touched: false },
          { id: 2, type: "Cr", ledgerId: defaultBankId, debitAmount: "", creditAmount: "", lineNarration: "", touched: false },
        ]);
      } else if (vt.id === 5) {
        setRows([
          { id: 1, type: "Dr", ledgerId: defaultBankId, debitAmount: "", creditAmount: "", lineNarration: "", touched: false },
          { id: 2, type: "Cr", ledgerId: "", debitAmount: "", creditAmount: "", lineNarration: "", touched: false },
        ]);
      } else if (vt.id === 7) {
        setRows([
          { id: 1, type: "Dr", ledgerId: defaultBankId, debitAmount: "", creditAmount: "", lineNarration: "", touched: false },
          { id: 2, type: "Cr", ledgerId: "", debitAmount: "", creditAmount: "", lineNarration: "", touched: false },
        ]);
      } else {
        setRows([
          { id: 1, type: "Dr", ledgerId: "", debitAmount: "", creditAmount: "", lineNarration: "", touched: false },
          { id: 2, type: "Cr", ledgerId: "", debitAmount: "", creditAmount: "", lineNarration: "", touched: false },
        ]);
      }
    }
  };

  // ─── Keyboard Shortcuts (F4 to F7) ──────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      const keyMap = {
        F4: VOUCHER_TYPES[0], // Contra
        F5: VOUCHER_TYPES[1], // Payment
        F6: VOUCHER_TYPES[2], // Receipt
        F7: VOUCHER_TYPES[3], // Journal
      };

      if (keyMap[e.key]) {
        e.preventDefault();
        handleSelectVoucherType(keyMap[e.key]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [ledgers, editingVoucherId]);

  // ─── Filter Available Ledgers for a Specific Row ────────────────────────────
  const getAvailableLedgersForRow = (row) => {
    if (!ledgers || ledgers.length === 0) return [];

    if (voucherType.id === 7) {
      return ledgers.filter(isCashOrBankLedger);
    }
    if (voucherType.id === 4) {
      if (row.type === "Cr") return ledgers.filter(isCashOrBankLedger);
      return ledgers;
    }
    if (voucherType.id === 5) {
      if (row.type === "Dr") return ledgers.filter(isCashOrBankLedger);
      return ledgers;
    }
    return ledgers;
  };

  // ─── Comprehensive Validation Rules ─────────────────────────────────────────
  const validationErrors = useMemo(() => {
    const errors = [];
    const rowErrors = {};

    if (!voucherDate) {
      errors.push("Voucher Date is required.");
    }
    if (!voucherNo || !voucherNo.trim()) {
      errors.push("Voucher Number cannot be blank.");
    }
    if (!narration || !narration.trim()) {
      errors.push("Narration is required (must contain 'Manual Entry').");
    } else if (narration.trim().length > 500) {
      errors.push("Narration exceeds maximum limit of 500 characters.");
    }

    let drCount = 0;
    let crCount = 0;
    let drTotal = 0;
    let crTotal = 0;

    rows.forEach((r) => {
      const rErr = [];
      const drAmt = Number(r.debitAmount || 0);
      const crAmt = Number(r.creditAmount || 0);

      const hasAmount = drAmt > 0 || crAmt > 0;
      const hasLedger = Boolean(r.ledgerId);

      if (hasAmount && !hasLedger) {
        rErr.push("Please select a Ledger Account for this line.");
      }
      if (hasLedger && !hasAmount) {
        rErr.push("Amount must be greater than 0.00.");
      }

      if (hasLedger) {
        const selectedLedgerObj = ledgers.find((l) => Number(l.ledgerId) === Number(r.ledgerId));
        const isCashBank = isCashOrBankLedger(selectedLedgerObj);

        if (voucherType.id === 7 && !isCashBank) {
          rErr.push("Contra entries only allow Cash & Bank accounts.");
        }
        if (voucherType.id === 4 && r.type === "Cr" && !isCashBank) {
          rErr.push("Payment (Cr) must be from a Cash or Bank account.");
        }
        if (voucherType.id === 5 && r.type === "Dr" && !isCashBank) {
          rErr.push("Receipt (Dr) must be deposited into a Cash or Bank account.");
        }
      }

      if (r.type === "Dr") {
        if (r.debitAmount && isNaN(Number(r.debitAmount))) {
          rErr.push("Debit amount must be a valid number.");
        } else if (r.debitAmount && Number(r.debitAmount) < 0) {
          rErr.push("Negative amounts are not allowed.");
        } else if (drAmt > 0 && hasLedger) {
          drCount++;
          drTotal += drAmt;
        }
      } else {
        if (r.creditAmount && isNaN(Number(r.creditAmount))) {
          rErr.push("Credit amount must be a valid number.");
        } else if (r.creditAmount && Number(r.creditAmount) < 0) {
          rErr.push("Negative amounts are not allowed.");
        } else if (crAmt > 0 && hasLedger) {
          crCount++;
          crTotal += crAmt;
        }
      }

      if (rErr.length > 0) {
        rowErrors[r.id] = rErr;
      }
    });

    if (drCount === 0) {
      errors.push("At least one valid Debit (Dr) line with positive amount is required.");
    }
    if (crCount === 0) {
      errors.push("At least one valid Credit (Cr) line with positive amount is required.");
    }

    if (drTotal <= 0 || crTotal <= 0) {
      errors.push("Voucher Total Amount must be greater than ₹0.00.");
    }

    const diff = Math.abs(drTotal - crTotal);
    if (diff > 0.001) {
      errors.push(`Debit and Credit must balance exactly. Difference: ₹${diff.toFixed(2)}.`);
    }

    if (rows.length === 2 && rows[0].ledgerId && rows[1].ledgerId && rows[0].ledgerId === rows[1].ledgerId) {
      errors.push("Debit and Credit accounts cannot be identical on the same entry.");
    }

    const isValid = errors.length === 0 && Object.keys(rowErrors).length === 0;

    return {
      isValid,
      errors,
      rowErrors,
      totalDebit: drTotal,
      totalCredit: crTotal,
      difference: diff,
      isBalanced: diff < 0.001 && drTotal > 0 && crTotal > 0,
    };
  }, [voucherDate, voucherNo, narration, rows, voucherType, ledgers]);

  // ─── Row Handlers ───────────────────────────────────────────────────────────
  const handleTypeChange = (id, newType) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        return {
          ...r,
          type: newType,
          debitAmount: newType === "Dr" ? r.creditAmount || r.debitAmount : "",
          creditAmount: newType === "Cr" ? r.debitAmount || r.creditAmount : "",
          touched: true,
        };
      })
    );
  };

  const handleLedgerChange = (id, ledgerId) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ledgerId: ledgerId ? Number(ledgerId) : "", touched: true } : r))
    );
  };

  const handleAmountChange = (id, val) => {
    if (val !== "" && !/^\d*\.?\d{0,2}$/.test(val)) return;

    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        if (r.type === "Dr") {
          return { ...r, debitAmount: val, creditAmount: "", touched: true };
        } else {
          return { ...r, creditAmount: val, debitAmount: "", touched: true };
        }
      })
    );
  };

  const addRow = (defaultType = "Dr") => {
    let autoAmt = "";
    if (defaultType === "Cr" && validationErrors.totalDebit > validationErrors.totalCredit) {
      autoAmt = (validationErrors.totalDebit - validationErrors.totalCredit).toFixed(2);
    } else if (defaultType === "Dr" && validationErrors.totalCredit > validationErrors.totalDebit) {
      autoAmt = (validationErrors.totalCredit - validationErrors.totalDebit).toFixed(2);
    }

    let defaultLedger = "";
    if (voucherType.id === 4 && defaultType === "Cr") {
      const b = ledgers.find(isCashOrBankLedger);
      if (b) defaultLedger = b.ledgerId;
    } else if (voucherType.id === 5 && defaultType === "Dr") {
      const b = ledgers.find(isCashOrBankLedger);
      if (b) defaultLedger = b.ledgerId;
    }

    setRows((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: defaultType,
        ledgerId: defaultLedger,
        debitAmount: defaultType === "Dr" ? autoAmt : "",
        creditAmount: defaultType === "Cr" ? autoAmt : "",
        lineNarration: "",
        touched: false,
      },
    ]);
  };

  const removeRow = (id) => {
    if (rows.length <= 2) return;
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handleAutoBalance = () => {
    if (validationErrors.difference <= 0.001) return;

    if (validationErrors.totalDebit > validationErrors.totalCredit) {
      const diff = (validationErrors.totalDebit - validationErrors.totalCredit).toFixed(2);
      const emptyCr = rows.find((r) => r.type === "Cr" && !r.creditAmount);
      if (emptyCr) {
        setRows((prev) => prev.map((r) => (r.id === emptyCr.id ? { ...r, creditAmount: diff } : r)));
      } else {
        addRow("Cr");
      }
    } else {
      const diff = (validationErrors.totalCredit - validationErrors.totalDebit).toFixed(2);
      const emptyDr = rows.find((r) => r.type === "Dr" && !r.debitAmount);
      if (emptyDr) {
        setRows((prev) => prev.map((r) => (r.id === emptyDr.id ? { ...r, debitAmount: diff } : r)));
      } else {
        addRow("Dr");
      }
    }
  };

  // ─── Edit Voucher Handler ──────────────────────────────────────────────────
  const handleEditVoucher = (v) => {
    const matchingType = VOUCHER_TYPES.find((vt) => vt.id === Number(v.voucherTypeId)) || VOUCHER_TYPES[3];
    setEditingVoucherId(Number(v.voucherId));
    setVoucherType(matchingType);
    setVoucherNo(v.voucherNo || "");
    setVoucherDate(v.voucherDate ? v.voucherDate.split("T")[0] : new Date().toISOString().split("T")[0]);
    setNarration(v.narration || "Manual Entry");

    if (v.lines && v.lines.length > 0) {
      const newRows = [];
      let rowCounter = 1;

      v.lines.forEach((l) => {
        if (l.drLedgerId) {
          newRows.push({
            id: rowCounter++,
            type: "Dr",
            ledgerId: Number(l.drLedgerId),
            debitAmount: String(l.amount || ""),
            creditAmount: "",
            lineNarration: l.lineNarration || "",
            touched: true,
          });
        }
        if (l.crLedgerId) {
          newRows.push({
            id: rowCounter++,
            type: "Cr",
            ledgerId: Number(l.crLedgerId),
            debitAmount: "",
            creditAmount: String(l.amount || ""),
            lineNarration: l.lineNarration || "",
            touched: true,
          });
        }
      });

      if (newRows.length >= 2) {
        setRows(newRows);
      } else {
        setRows([
          { id: 1, type: "Dr", ledgerId: Number(v.lines[0]?.drLedgerId || ""), debitAmount: String(v.totalAmount || ""), creditAmount: "", lineNarration: "", touched: true },
          { id: 2, type: "Cr", ledgerId: Number(v.lines[0]?.crLedgerId || ""), debitAmount: "", creditAmount: String(v.totalAmount || ""), lineNarration: "", touched: true },
        ]);
      }
    }

    setSubmittedAttempt(false);
    setFeedback({
      type: "info",
      message: `Editing Voucher #${v.voucherNo}. Make your changes and click 'Update Voucher'.`,
    });

    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleCancelEdit = () => {
    setEditingVoucherId(0);
    handleSelectVoucherType(voucherType);
    setNarration("Manual Entry");
    setSubmittedAttempt(false);
    setVoucherNo(generateVoucherNo(voucherType, voucherDate));
    setFeedback({ type: "", message: "" });
  };

  // ─── Delete Voucher Handler ────────────────────────────────────────────────
  const handleDeleteVoucher = async () => {
    if (!deleteModal.voucher) return;
    setDeleteModal((prev) => ({ ...prev, deleting: true }));

    try {
      const vId = deleteModal.voucher.voucherId;
      const res = await api.delete(
        `${API_WEB_URLS.VOUCHER_ENTRY}/${AUTH_USER_ID}/${AUTH_TOKEN}/Delete/${vId}`
      );

      if (res?.data?.success || res?.data?.Success) {
        setFeedback({
          type: "success",
          message: `Voucher #${deleteModal.voucher.voucherNo} deleted successfully.`,
        });

        if (editingVoucherId === vId) {
          handleCancelEdit();
        }

        setDeleteModal({ show: false, voucher: null, deleting: false });
        fetchRecentVouchers();
      } else {
        setFeedback({
          type: "danger",
          message: res?.data?.message || "Failed to delete voucher.",
        });
        setDeleteModal((prev) => ({ ...prev, deleting: false }));
      }
    } catch (err) {
      setFeedback({
        type: "danger",
        message: err?.response?.data?.message || err?.message || "Error deleting voucher.",
      });
      setDeleteModal((prev) => ({ ...prev, deleting: false }));
    }
  };

  // ─── Submit / Save or Update Voucher ────────────────────────────────────────
  const handleSaveVoucher = async (e) => {
    if (e) e.preventDefault();
    setSubmittedAttempt(true);

    if (!validationErrors.isValid) {
      setFeedback({
        type: "danger",
        message: validationErrors.errors[0] || "Please resolve all validation errors before saving.",
      });
      return;
    }

    const drRows = rows.filter((r) => r.type === "Dr" && r.ledgerId && Number(r.debitAmount) > 0);
    const crRows = rows.filter((r) => r.type === "Cr" && r.ledgerId && Number(r.creditAmount) > 0);

    const lines = [];
    if (drRows.length === 1 && crRows.length === 1) {
      lines.push({
        drLedgerId: drRows[0].ledgerId,
        crLedgerId: crRows[0].ledgerId,
        amount: Number(drRows[0].debitAmount),
        lineNarration: drRows[0].lineNarration || narration,
      });
    } else {
      let crIdx = 0;
      let crRemaining = Number(crRows[0].creditAmount);

      for (let d = 0; d < drRows.length; d++) {
        let drRemaining = Number(drRows[d].debitAmount);
        while (drRemaining > 0.001 && crIdx < crRows.length) {
          const matchAmt = Math.min(drRemaining, crRemaining);
          lines.push({
            drLedgerId: drRows[d].ledgerId,
            crLedgerId: crRows[crIdx].ledgerId,
            amount: matchAmt,
            lineNarration: drRows[d].lineNarration || crRows[crIdx].lineNarration || narration,
          });
          drRemaining -= matchAmt;
          crRemaining -= matchAmt;
          if (crRemaining <= 0.001) {
            crIdx++;
            if (crIdx < crRows.length) {
              crRemaining = Number(crRows[crIdx].creditAmount);
            }
          }
        }
      }
    }

    let finalNarration = narration.trim();
    if (!finalNarration) {
      finalNarration = "Manual Entry";
    } else if (!finalNarration.toLowerCase().includes("manual entry")) {
      finalNarration = `Manual Entry - ${finalNarration}`;
    }

    const payload = {
      voucherId: editingVoucherId || 0,
      voucherNo: voucherNo.trim(),
      voucherTypeId: voucherType.id,
      voucherDate: new Date(voucherDate).toISOString(),
      narration: finalNarration,
      totalAmount: validationErrors.totalDebit,
      lines: lines,
    };

    setSaving(true);
    setFeedback({ type: "", message: "" });

    try {
      const res = await api.post(
        `${API_WEB_URLS.VOUCHER_ENTRY}/${AUTH_USER_ID}/${AUTH_TOKEN}/Save`,
        payload
      );

      if (res?.data?.success || res?.data?.Success) {
        setFeedback({
          type: "success",
          message: editingVoucherId
            ? `Voucher ${voucherNo} updated successfully!`
            : `Voucher ${voucherNo} saved successfully! Narration: "${finalNarration}"`,
        });

        setEditingVoucherId(0);
        handleSelectVoucherType(voucherType);
        setNarration("Manual Entry");
        setSubmittedAttempt(false);
        setVoucherNo(generateVoucherNo(voucherType, voucherDate));

        fetchRecentVouchers();
      } else {
        setFeedback({
          type: "danger",
          message: res?.data?.message || "Failed to save voucher.",
        });
      }
    } catch (err) {
      setFeedback({
        type: "danger",
        message: err?.response?.data?.message || err?.message || "Error saving voucher.",
      });
    }

    setSaving(false);
  };

  // ─── Filter & Pagination for History Table ──────────────────────────────────
  const filteredVouchers = useMemo(() => {
    if (!searchQuery.trim()) return recentVouchers;
    const q = searchQuery.toLowerCase().trim();

    return recentVouchers.filter((v) => {
      const noMatch = (v.voucherNo || "").toLowerCase().includes(q);
      const typeMatch = (v.voucherTypeName || "").toLowerCase().includes(q);
      const narrationMatch = (v.narration || "").toLowerCase().includes(q);
      const dateMatch = (v.voucherDate || "").toLowerCase().includes(q);
      const amountMatch = String(v.totalAmount || "").includes(q);

      const linesMatch = (v.lines || []).some(
        (l) =>
          (l.drLedgerName || "").toLowerCase().includes(q) ||
          (l.crLedgerName || "").toLowerCase().includes(q) ||
          (l.drLedgerCode || "").toLowerCase().includes(q) ||
          (l.crLedgerCode || "").toLowerCase().includes(q)
      );

      return noMatch || typeMatch || narrationMatch || dateMatch || amountMatch || linesMatch;
    });
  }, [recentVouchers, searchQuery]);

  const totalPages = Math.ceil(filteredVouchers.length / pageSize) || 1;
  const paginatedVouchers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredVouchers.slice(start, start + pageSize);
  }, [filteredVouchers, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pageSize]);

  return (
    <div className="page-content py-4 px-3 px-md-4" ref={formRef}>
      {/* ─── Header ────────────────────────────────────────────────────────── */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h4 className="mb-1 fw-bold text-dark d-flex align-items-center">
            <i className="bx bx-receipt me-2 text-primary"></i>
            Accounting Voucher Entry
          </h4>
          <p className="text-muted mb-0 small">
            Double-entry vouchers (Contra, Payment, Receipt, Journal) with automated Bank/Cash rules and full history.
          </p>
        </div>

        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={fetchRecentVouchers} disabled={loadingRecent}>
            <i className="bx bx-history me-1"></i> Refresh History
          </Button>
        </div>
      </div>

      {/* Alert Feedback Banner */}
      {feedback.message && (
        <Alert
          variant={feedback.type}
          dismissible
          onClose={() => setFeedback({ type: "", message: "" })}
          className="shadow-sm border-0"
        >
          <i className={`bx ${feedback.type === "success" ? "bx-check-circle" : feedback.type === "info" ? "bx-info-circle" : "bx-error-circle"} me-2 fs-5 align-middle`}></i>
          {feedback.message}
        </Alert>
      )}

      {/* Editing Mode Notice Banner */}
      {editingVoucherId > 0 && (
        <Alert variant="warning" className="d-flex justify-content-between align-items-center shadow-sm mb-3">
          <div className="d-flex align-items-center">
            <i className="bx bx-edit fs-4 me-2"></i>
            <div>
              <strong>Editing Mode:</strong> Updating existing Voucher <strong>#{voucherNo}</strong>.
            </div>
          </div>
          <Button variant="outline-dark" size="sm" onClick={handleCancelEdit}>
            <i className="bx bx-x me-1"></i> Cancel Edit
          </Button>
        </Alert>
      )}

      {/* ─── Voucher Type Selector Buttons ──────────────────────────────────── */}
      <Card className="shadow-sm border-0 mb-4 rounded-3 bg-light">
        <Card.Body className="p-3">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex flex-wrap gap-2">
              {VOUCHER_TYPES.map((vt) => {
                const isActive = voucherType.id === vt.id;
                return (
                  <Button
                    key={vt.id}
                    variant={isActive ? vt.color : "outline-secondary"}
                    className={`px-3 py-2 fw-semibold d-flex align-items-center shadow-sm ${isActive ? "text-white" : "bg-white"}`}
                    onClick={() => handleSelectVoucherType(vt)}
                    title={vt.desc}
                  >
                    <i className={`bx ${vt.icon} me-2 fs-5`}></i>
                    <span>{vt.name}</span>
                  </Button>
                );
              })}
            </div>
            <span className="badge bg-secondary px-2 py-1 small">
              <i className="bx bx-keyboard me-1"></i> Press F4 to F7 to switch
            </span>
          </div>

          {/* Type Rule Notice */}
          <div className="mt-2 pt-2 border-top small text-muted d-flex align-items-center">
            <i className="bx bx-info-circle me-1 text-primary"></i>
            <span className="fw-medium text-dark me-1">{voucherType.name}:</span>
            {voucherType.id === 7 && "Both Debit (Dr) and Credit (Cr) must be Cash & Bank accounts."}
            {voucherType.id === 4 && "Credit (Cr) side is fixed to Cash & Bank accounts (Money going OUT)."}
            {voucherType.id === 5 && "Debit (Dr) side is fixed to Cash & Bank accounts (Money coming IN)."}
            {voucherType.id === 6 && "Standard journal adjustment entries across general ledger accounts."}
          </div>
        </Card.Body>
      </Card>

      {/* ─── Main Voucher Entry Card ───────────────────────────────────────── */}
      <Card className="shadow-sm border-0 rounded-3 mb-4 overflow-hidden">
        <Card.Header
          className={`py-3 text-white d-flex flex-wrap justify-content-between align-items-center bg-${voucherType.color}`}
        >
          <div className="d-flex align-items-center gap-3">
            <h5 className="mb-0 fw-bold">
              <i className={`bx ${voucherType.icon} me-2`}></i>
              {voucherType.short.toUpperCase()} VOUCHER
            </h5>
            <Badge bg="light" className="text-dark fs-6 px-3 py-1">
              #{voucherNo}
            </Badge>
            {editingVoucherId > 0 && (
              <Badge bg="warning" className="text-dark fs-6 px-2 py-1">
                EDITING #{editingVoucherId}
              </Badge>
            )}
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="small opacity-75">Date:</span>
            <Form.Control
              type="date"
              size="sm"
              className="fw-bold bg-white text-dark border-0 shadow-sm"
              style={{ width: 160 }}
              value={voucherDate}
              onChange={(e) => setVoucherDate(e.target.value)}
              required
            />
          </div>
        </Card.Header>

        <Card.Body className="p-4">
          <Form onSubmit={handleSaveVoucher}>
            {/* Voucher Number & Narration Header Controls */}
            <Row className="g-3 mb-4">
              <Col md={3}>
                <Form.Label className="small fw-semibold text-secondary">
                  Voucher No <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  className={`fw-bold ${submittedAttempt && (!voucherNo || !voucherNo.trim()) ? "is-invalid" : ""}`}
                  value={voucherNo}
                  onChange={(e) => setVoucherNo(e.target.value)}
                  placeholder="Auto-generated"
                  required
                />
              </Col>

              <Col md={9}>
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label className="small fw-semibold text-secondary mb-1">
                    Voucher Narration / Remarks <span className="text-danger">* (Auto: "Manual Entry")</span>
                  </Form.Label>
                  <span className="small text-muted">{narration.length}/500 chars</span>
                </div>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="e.g. Manual Entry - Office Electricity Bill Payment"
                  value={narration}
                  maxLength={500}
                  className={submittedAttempt && (!narration || !narration.trim()) ? "is-invalid" : ""}
                  onChange={(e) => setNarration(e.target.value)}
                  required
                />
              </Col>
            </Row>

            {/* ─── Multi-Row Dr/Cr Table with Searchable React-Select ─────── */}
            <div className="table-responsive mb-3 rounded-2 border" style={{ overflow: "visible" }}>
              <Table className="mb-0 align-middle">
                <thead className="table-dark text-uppercase small">
                  <tr>
                    <th style={{ width: "12%" }}>Type</th>
                    <th style={{ width: "42%" }}>
                      Particulars (Ledger Account) <span className="text-danger">*</span>
                    </th>
                    <th className="text-end" style={{ width: "18%" }}>Debit (Dr ₹)</th>
                    <th className="text-end" style={{ width: "18%" }}>Credit (Cr ₹)</th>
                    <th className="text-center" style={{ width: "10%" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const isDr = row.type === "Dr";
                    const availableLedgers = getAvailableLedgersForRow(row);
                    const isRowFixedBank =
                      (voucherType.id === 7) ||
                      (voucherType.id === 4 && !isDr) ||
                      (voucherType.id === 5 && isDr);

                    const ledgerOptions = availableLedgers.map((l) => ({
                      value: l.ledgerId,
                      label: `${l.ledgerName} ${l.ledgerCode ? `(${l.ledgerCode})` : ""} - [${l.ledgerGroupName}]`,
                      ledger: l,
                    }));

                    const selectedOption = ledgerOptions.find((opt) => Number(opt.value) === Number(row.ledgerId)) || null;

                    const hasRowError = (submittedAttempt || row.touched) && validationErrors.rowErrors[row.id];
                    const isAmountMissing = (submittedAttempt || row.touched) && row.ledgerId && !row.debitAmount && !row.creditAmount;
                    const isLedgerMissing = (submittedAttempt || row.touched) && (row.debitAmount > 0 || row.creditAmount > 0) && !row.ledgerId;

                    return (
                      <React.Fragment key={row.id}>
                        <tr className={isDr ? "table-light" : "bg-white"}>
                          {/* Type: Dr / Cr Selector */}
                          <td>
                            <Form.Select
                              size="sm"
                              className={`fw-bold text-center ${isDr ? "text-danger border-danger" : "text-success border-success"}`}
                              value={row.type}
                              onChange={(e) => handleTypeChange(row.id, e.target.value)}
                            >
                              <option value="Dr">Dr (By)</option>
                              <option value="Cr">Cr (To)</option>
                            </Form.Select>
                          </td>

                          {/* Particulars: React-Select Searchable Dropdown */}
                          <td>
                            <div className="d-flex flex-column gap-1">
                              <Select
                                value={selectedOption}
                                options={ledgerOptions}
                                onChange={(opt) => handleLedgerChange(row.id, opt ? opt.value : "")}
                                placeholder={isRowFixedBank ? "Search Cash / Bank Account..." : "Search Ledger Account..."}
                                isClearable
                                isSearchable
                                isLoading={loadingLedgers}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={isLedgerMissing ? customSelectErrorStyles : customSelectStyles}
                                noOptionsMessage={() => "No ledger accounts found"}
                              />
                              {isRowFixedBank && (
                                <span className="small text-muted" style={{ fontSize: "0.75rem" }}>
                                  <i className="bx bx-check-shield text-success me-1"></i>
                                  {voucherType.id === 4 ? "Bank/Cash (Paying A/c)" : voucherType.id === 5 ? "Bank/Cash (Receiving A/c)" : "Cash/Bank Account"}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Debit Amount */}
                          <td>
                            <Form.Control
                              type="text"
                              inputMode="decimal"
                              size="sm"
                              className={`text-end fw-bold ${isDr && isAmountMissing ? "is-invalid border-danger" : ""}`}
                              placeholder={isDr ? "0.00" : ""}
                              disabled={!isDr}
                              value={isDr ? row.debitAmount : ""}
                              onChange={(e) => handleAmountChange(row.id, e.target.value)}
                            />
                          </td>

                          {/* Credit Amount */}
                          <td>
                            <Form.Control
                              type="text"
                              inputMode="decimal"
                              size="sm"
                              className={`text-end fw-bold ${!isDr && isAmountMissing ? "is-invalid border-danger" : ""}`}
                              placeholder={!isDr ? "0.00" : ""}
                              disabled={isDr}
                              value={!isDr ? row.creditAmount : ""}
                              onChange={(e) => handleAmountChange(row.id, e.target.value)}
                            />
                          </td>

                          {/* Action: Delete Row */}
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="p-1 px-2"
                              disabled={rows.length <= 2}
                              onClick={() => removeRow(row.id)}
                              title={rows.length <= 2 ? "Minimum 2 lines required" : "Remove row"}
                            >
                              <i className="bx bx-trash"></i>
                            </Button>
                          </td>
                        </tr>

                        {/* Inline Row Validation Error Message */}
                        {hasRowError && (
                          <tr className="bg-danger bg-opacity-10 border-0">
                            <td colSpan={5} className="py-1 px-3 text-danger small">
                              <i className="bx bx-error-circle me-1"></i>
                              {validationErrors.rowErrors[row.id].join(" ")}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>

                {/* Table Footer: Totals & Balancing */}
                <tfoot className="table-light border-top-2 fw-bold">
                  <tr>
                    <td colSpan={2} className="text-end">TOTAL (₹):</td>
                    <td className="text-end text-danger fs-6">
                      ₹{validationErrors.totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="text-end text-success fs-6">
                      ₹{validationErrors.totalCredit.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            </div>

            {/* Row Actions + Live Balancing Status Banner */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-3 rounded-2 bg-light border mb-4">
              <div className="d-flex gap-2 flex-wrap">
                <Button variant="outline-primary" size="sm" onClick={() => addRow("Dr")}>
                  <i className="bx bx-plus-circle me-1"></i> + Add Dr Line
                </Button>
                <Button variant="outline-success" size="sm" onClick={() => addRow("Cr")}>
                  <i className="bx bx-plus-circle me-1"></i> + Add Cr Line
                </Button>
                {!validationErrors.isBalanced && validationErrors.difference > 0.001 && (
                  <Button variant="warning" size="sm" className="fw-semibold" onClick={handleAutoBalance}>
                    <i className="bx bx-calculator me-1"></i> Auto-Balance (₹{validationErrors.difference.toFixed(2)})
                  </Button>
                )}
              </div>

              {/* Balancing Status Badge */}
              <div className="d-flex align-items-center gap-3">
                {validationErrors.isBalanced ? (
                  <Badge bg="success" className="px-3 py-2 fs-6 d-flex align-items-center shadow-sm">
                    <i className="bx bx-check-double me-1 fs-5"></i>
                    BALANCED (Difference: ₹0.00)
                  </Badge>
                ) : (
                  <Badge bg="danger" className="px-3 py-2 fs-6 d-flex align-items-center shadow-sm">
                    <i className="bx bx-error-circle me-1 fs-5"></i>
                    DIFFERENCE: ₹{validationErrors.difference.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Badge>
                )}
              </div>
            </div>

            {/* Validation Errors List Banner */}
            {submittedAttempt && !validationErrors.isValid && (
              <Alert variant="danger" className="py-2 px-3 mb-4 shadow-sm">
                <div className="fw-bold mb-1 small d-flex align-items-center">
                  <i className="bx bx-error me-1 fs-5"></i> Please resolve the following errors before saving:
                </div>
                <ul className="mb-0 small ps-3">
                  {validationErrors.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {/* Submit / Save or Update Button Bar */}
            <div className="d-flex justify-content-end gap-3">
              {editingVoucherId > 0 && (
                <Button variant="outline-dark" size="lg" onClick={handleCancelEdit}>
                  Cancel Edit
                </Button>
              )}

              <Button
                variant="secondary"
                size="lg"
                onClick={() => {
                  if (editingVoucherId > 0) {
                    handleCancelEdit();
                  } else {
                    handleSelectVoucherType(voucherType);
                    setNarration("Manual Entry");
                    setSubmittedAttempt(false);
                    setFeedback({ type: "", message: "" });
                  }
                }}
              >
                Reset Form
              </Button>

              <Button
                type="submit"
                variant={validationErrors.isValid ? (editingVoucherId ? "warning" : "primary") : "secondary"}
                size="lg"
                disabled={!validationErrors.isValid || saving}
                className={`px-5 fw-bold shadow ${editingVoucherId ? "text-dark" : ""}`}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingVoucherId ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <i className={`bx ${editingVoucherId ? "bx-check-circle" : "bx-save"} me-2`}></i>
                    {editingVoucherId ? "Update Voucher" : "Save Voucher (Ctrl+S)"}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* ─── Recent Manual Vouchers History Card with Search, Pagination & Actions ─── */}
      <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
        <Card.Header className="bg-light py-3 border-bottom">
          <Row className="align-items-center g-2">
            <Col md={4}>
              <h6 className="mb-0 fw-bold text-dark d-flex align-items-center">
                <i className="bx bx-history me-2 text-primary"></i>
                Recent Manual Vouchers History
                <Badge bg="primary" className="ms-2">
                  {filteredVouchers.length}
                </Badge>
              </h6>
            </Col>

            {/* Search Input Box */}
            <Col md={5}>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bx bx-search text-muted"></i>
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search by Voucher #, Type, Ledger, Narration, Amount..."
                  className="border-start-0 ps-0"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <Button variant="outline-secondary" size="sm" onClick={() => setSearchQuery("")}>
                    <i className="bx bx-x"></i>
                  </Button>
                )}
              </div>
            </Col>

            {/* Page Size Selector */}
            <Col md={3} className="d-flex justify-content-md-end align-items-center gap-2">
              <span className="small text-muted text-nowrap">Rows:</span>
              <Form.Select
                size="sm"
                style={{ width: 80 }}
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead className="table-light small text-uppercase">
                <tr>
                  <th>Voucher #</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Narration</th>
                  <th>Debited Ledger (Dr)</th>
                  <th>Credited Ledger (Cr)</th>
                  <th className="text-end">Amount (₹)</th>
                  <th className="text-center" style={{ width: "120px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingRecent ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <span className="ms-2 small text-muted">Loading vouchers...</span>
                    </td>
                  </tr>
                ) : paginatedVouchers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4 text-muted">
                      {searchQuery ? "No vouchers match your search query." : "No manual vouchers recorded yet."}
                    </td>
                  </tr>
                ) : (
                  paginatedVouchers.map((v) => (
                    <tr key={v.voucherId} className={editingVoucherId === Number(v.voucherId) ? "table-warning" : ""}>
                      <td className="fw-bold text-primary">
                        {v.voucherNo}
                        {editingVoucherId === Number(v.voucherId) && (
                          <Badge bg="warning" className="text-dark ms-1 small">
                            Editing
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Badge bg="light" className="text-dark border">
                          {v.voucherTypeName}
                        </Badge>
                      </td>
                      <td className="small text-muted">{v.voucherDate ? v.voucherDate.split("T")[0] : "-"}</td>
                      <td className="small">
                        <span className="badge bg-warning bg-opacity-25 text-dark me-1">Manual</span>
                        {v.narration}
                      </td>
                      <td>
                        {v.lines && v.lines.length > 0 ? (
                          v.lines.map((l, idx) => (
                            <div key={idx} className="small text-danger fw-semibold">
                              • {l.drLedgerName || `Ledger #${l.drLedgerId}`}
                            </div>
                          ))
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {v.lines && v.lines.length > 0 ? (
                          v.lines.map((l, idx) => (
                            <div key={idx} className="small text-success fw-semibold">
                              • {l.crLedgerName || `Ledger #${l.crLedgerId}`}
                            </div>
                          ))
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-end fw-bold text-dark fs-6">
                        ₹{Number(v.totalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      {/* Action Buttons: Edit & Delete */}
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="p-1 px-2"
                            title="Edit this Voucher"
                            onClick={() => handleEditVoucher(v)}
                          >
                            <i className="bx bx-edit"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="p-1 px-2"
                            title="Delete this Voucher"
                            onClick={() => setDeleteModal({ show: true, voucher: v, deleting: false })}
                          >
                            <i className="bx bx-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>

        {/* ─── Pagination Footer ────────────────────────────────────────────── */}
        {filteredVouchers.length > 0 && (
          <Card.Footer className="bg-white py-3 border-top d-flex flex-wrap justify-content-between align-items-center gap-2">
            <span className="small text-muted">
              Showing {(currentPage - 1) * pageSize + 1} to{" "}
              {Math.min(currentPage * pageSize, filteredVouchers.length)} of {filteredVouchers.length} entries
            </span>

            {totalPages > 1 && (
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                />
                {[...Array(totalPages)].map((_, idx) => {
                  const page = idx + 1;
                  // Show max 5 pages centered
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  ) {
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  }
                  if (page === currentPage - 3 || page === currentPage + 3) {
                    return <Pagination.Ellipsis key={page} disabled />;
                  }
                  return null;
                })}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                />
              </Pagination>
            )}
          </Card.Footer>
        )}
      </Card>

      {/* ─── Delete Confirmation Modal ────────────────────────────────────── */}
      <Modal
        show={deleteModal.show}
        onHide={() => !deleteModal.deleting && setDeleteModal({ show: false, voucher: null, deleting: false })}
        centered
      >
        <Modal.Header closeButton={!deleteModal.deleting} className="bg-danger text-white">
          <Modal.Title className="fs-6 fw-bold">
            <i className="bx bx-trash me-2"></i> Confirm Delete Voucher
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          <p className="mb-2">Are you sure you want to permanently delete this manual voucher entry?</p>
          {deleteModal.voucher && (
            <div className="p-3 bg-light rounded-2 border">
              <div>
                <strong>Voucher No:</strong> <span className="text-primary">{deleteModal.voucher.voucherNo}</span>
              </div>
              <div>
                <strong>Type:</strong> {deleteModal.voucher.voucherTypeName}
              </div>
              <div>
                <strong>Date:</strong> {deleteModal.voucher.voucherDate ? deleteModal.voucher.voucherDate.split("T")[0] : "-"}
              </div>
              <div>
                <strong>Total Amount:</strong> ₹{Number(deleteModal.voucher.totalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="small text-muted mt-1">
                <strong>Narration:</strong> {deleteModal.voucher.narration}
              </div>
            </div>
          )}
          <div className="text-danger small mt-3">
            <i className="bx bx-error-circle me-1"></i> This action cannot be undone and will reverse the double-entry impact on ledger balances.
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            disabled={deleteModal.deleting}
            onClick={() => setDeleteModal({ show: false, voucher: null, deleting: false })}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={deleteModal.deleting}
            onClick={handleDeleteVoucher}
          >
            {deleteModal.deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" /> Deleting...
              </>
            ) : (
              <>
                <i className="bx bx-trash me-1"></i> Yes, Delete Voucher
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VoucherEntry;
